import type { IntakeSpec, FurnitureChairDetails } from '@shared/types';
import type { Part, Joint, Feature } from '@shared/spec';
import { LEG_CROSS_SECTION, PANEL_THICKNESS } from '@/lib/intakeBuilderDefaults';

function getDetails(intake: IntakeSpec): FurnitureChairDetails {
  return intake.details as FurnitureChairDetails;
}

export function parts(intake: IntakeSpec): Part[] {
  const d = getDetails(intake);
  const matKind = intake.core.primaryMaterial ?? 'hardwood';
  const panelThickness =
    matKind === 'plywood' || matKind === 'mdf' ? PANEL_THICKNESS.plywood : PANEL_THICKNESS.hardwood;

  const seatW = intake.core.size?.width ?? 450;
  const seatD = intake.core.size?.length ?? 450;
  const seatThickness = 30;
  const seatH = intake.core.size?.height ?? 450;
  const legLength = seatH - seatThickness;
  const legSection = LEG_CROSS_SECTION[d.legStyle] || 50;
  const legInset = 40;

  const out: Part[] = [
    {
      id: 'seat',
      name: '座面',
      kind: 'panel',
      materialId: 'mat-primary',
      width: seatW,
      depth: seatD,
      thickness: seatThickness,
      position: [0, 0, seatH - seatThickness],
      rotation: [0, 0, 0],
      tags: ['seat'],
      paramRefs: [],
      cutouts: [],
    },
  ];

  // 4 legs
  // TODO: rocker base, V2
  const legPositions: Array<[number, number, number]> = [
    [-(seatW / 2 - legInset), -(seatD / 2 - legInset), 0],
    [seatW / 2 - legInset, -(seatD / 2 - legInset), 0],
    [-(seatW / 2 - legInset), seatD / 2 - legInset, 0],
    [seatW / 2 - legInset, seatD / 2 - legInset, 0],
  ];
  for (let i = 0; i < 4; i++) {
    out.push({
      id: `leg_${i + 1}`,
      name: `脚 ${i + 1}`,
      kind: 'leg',
      materialId: 'mat-primary',
      length: legLength,
      crossSection: { shape: 'square', side: legSection },
      position: legPositions[i],
      rotation: [0, 0, 0],
      tags: ['leg'],
      paramRefs: [],
    });
  }

  // Back rails by backStyle
  const backHeight = 350;
  const backBaseZ = seatH;

  if (d.backStyle === 'spindle') {
    const spindleSpacing = 55; // ~(350 - 5*15) / 4
    for (let i = 0; i < 5; i++) {
      const xPos = -(2 * spindleSpacing) + i * spindleSpacing;
      out.push({
        id: `spindle_${i + 1}`,
        name: `スピンドル ${i + 1}`,
        kind: 'rail',
        materialId: 'mat-primary',
        length: backHeight,
        width: 15,
        thickness: 15,
        position: [xPos, seatD / 2 - legInset, backBaseZ],
        rotation: [0, 0, 0],
        tags: ['back', 'spindle'],
        paramRefs: [],
      });
    }
  } else if (d.backStyle === 'slat') {
    const slatSpacing = 80;
    for (let i = 0; i < 3; i++) {
      out.push({
        id: `slat_${i + 1}`,
        name: `スラット ${i + 1}`,
        kind: 'rail',
        materialId: 'mat-primary',
        length: seatW - 2 * legInset,
        width: 60,
        thickness: 10,
        position: [0, seatD / 2 - legInset, backBaseZ + i * slatSpacing + 30],
        rotation: [0, 0, 0],
        tags: ['back', 'slat'],
        paramRefs: [],
      });
    }
  } else if (d.backStyle === 'panel') {
    out.push({
      id: 'back_panel',
      name: '背板',
      kind: 'panel',
      materialId: 'mat-primary',
      width: seatW - 2 * legInset,
      depth: panelThickness,
      thickness: backHeight,
      position: [0, seatD / 2 - legInset, backBaseZ + backHeight / 2],
      rotation: [0, 0, 0],
      tags: ['back'],
      paramRefs: [],
      cutouts: [],
    });
  } else if (d.backStyle === 'upholstered') {
    out.push({
      id: 'back_panel',
      name: '背板（布張り）',
      kind: 'panel',
      materialId: 'mat-primary',
      width: seatW - 2 * legInset,
      depth: 40,
      thickness: backHeight,
      position: [0, seatD / 2 - legInset, backBaseZ + backHeight / 2],
      rotation: [0, 0, 0],
      tags: ['back', 'upholstered'],
      paramRefs: [],
      cutouts: [],
    });
  } else if (d.backStyle === 'mesh') {
    out.push({
      id: 'back_mesh',
      name: '背板（メッシュ）',
      kind: 'custom',
      materialId: 'mat-primary',
      boundingBox: { w: seatW - 2 * legInset, d: 5, h: backHeight },
      openscadSnippet: '// mesh back placeholder',
      position: [0, seatD / 2 - legInset, backBaseZ + backHeight / 2],
      rotation: [0, 0, 0],
      tags: ['back', 'mesh'],
      paramRefs: [],
    });
  }

  // Armrests
  if (d.hasArmrests) {
    const armrestZ = seatH + 200;
    out.push(
      {
        id: 'armrest_left',
        name: '肘掛け（左）',
        kind: 'rail',
        materialId: 'mat-primary',
        length: seatD,
        width: 30,
        thickness: 25,
        position: [-(seatW / 2 - legInset), 0, armrestZ],
        rotation: [0, 0, 0],
        tags: ['armrest'],
        paramRefs: [],
      },
      {
        id: 'armrest_right',
        name: '肘掛け（右）',
        kind: 'rail',
        materialId: 'mat-primary',
        length: seatD,
        width: 30,
        thickness: 25,
        position: [seatW / 2 - legInset, 0, armrestZ],
        rotation: [0, 0, 0],
        tags: ['armrest'],
        paramRefs: [],
      },
    );
  }

  return out;
}

export function joints(intake: IntakeSpec): Joint[] {
  const d = getDetails(intake);
  const joinery = d.joineryType;

  const seatW = intake.core.size?.width ?? 450;
  const seatD = intake.core.size?.length ?? 450;
  const seatThickness = 30;
  const seatH = intake.core.size?.height ?? 450;
  const legLength = seatH - seatThickness;
  const legSection = LEG_CROSS_SECTION[d.legStyle] || 50;
  const legInset = 40;

  // mortise_tenon: emit real MortiseTenonJoint at seat-to-leg corners and
  // (for panel back) back-to-back-leg pair. Each tenon protrudes from the
  // adjacent rail/panel into the leg.
  if (joinery === 'mortise_tenon') {
    const out: Joint[] = [];
    const tenonLength = 25;
    const meetZ = legLength - 30;
    const halfLeg = legSection / 2;
    // For chairs with no aprons modeled in parts(), we emit seat-to-leg MTs
    // anchored at the seat's four corners, axis along the diagonal — for V1
    // we approximate as 'x' axis for left/right legs and use the same shape.
    const seatLocalCorners: Array<[string, [number, number, number]]> = [
      ['leg_1', [-(seatW / 2 - legInset), -(seatD / 2 - legInset), 0]],
      ['leg_2', [seatW / 2 - legInset, -(seatD / 2 - legInset), 0]],
      ['leg_3', [-(seatW / 2 - legInset), seatD / 2 - legInset, 0]],
      ['leg_4', [seatW / 2 - legInset, seatD / 2 - legInset, 0]],
    ];
    let idx = 1;
    for (const [legId, seatCorner] of seatLocalCorners) {
      out.push({
        id: `mt_${idx++}`,
        fromPartId: 'seat',
        toPartId: legId,
        type: 'mortise_tenon',
        tenonWidth: 30,
        tenonHeight: 20,
        tenonLength,
        shoulder: 5,
        haunched: false,
        clearance: 0.2,
        axis: 'x',
        fromPosition: seatCorner,
        toPosition: [0, 0, meetZ + halfLeg],
      });
    }
    if (d.backStyle === 'panel') {
      const backHeight = 350;
      const backLocalLeft: [number, number, number] = [-(seatW / 2 - legInset), 0, -backHeight / 2];
      const backLocalRight: [number, number, number] = [seatW / 2 - legInset, 0, -backHeight / 2];
      out.push({
        id: `mt_${idx++}`,
        fromPartId: 'back_panel',
        toPartId: 'leg_3',
        type: 'mortise_tenon',
        tenonWidth: 30,
        tenonHeight: 20,
        tenonLength,
        shoulder: 5,
        haunched: false,
        clearance: 0.2,
        axis: 'x',
        fromPosition: backLocalLeft,
        toPosition: [0, 0, legLength - 20],
      });
      out.push({
        id: `mt_${idx++}`,
        fromPartId: 'back_panel',
        toPartId: 'leg_4',
        type: 'mortise_tenon',
        tenonWidth: 30,
        tenonHeight: 20,
        tenonLength,
        shoulder: 5,
        haunched: false,
        clearance: 0.2,
        axis: 'x',
        fromPosition: backLocalRight,
        toPosition: [0, 0, legLength - 20],
      });
    }
    return out;
  }

  // mixed → use dowel for seat-to-leg
  const effectiveJoinery = joinery === 'mixed' ? 'dowel' : joinery;

  const out: Joint[] = [];

  // 4 seat-to-leg joints
  for (let i = 0; i < 4; i++) {
    if (effectiveJoinery === 'dowel') {
      out.push({ id: `seat_to_leg_${i + 1}`, fromPartId: 'seat', toPartId: `leg_${i + 1}`, type: 'dowel', diameter: 8, length: 40, count: 2 });
    } else {
      out.push({ id: `seat_to_leg_${i + 1}`, fromPartId: 'seat', toPartId: `leg_${i + 1}`, type: 'screw', size: 'M6', length: 50, count: 2 });
    }
  }

  // Back-to-seat joints (back left/right)
  const backPartId = (d.backStyle === 'spindle') ? 'spindle_1' :
    (d.backStyle === 'slat') ? 'slat_1' :
    (d.backStyle === 'mesh') ? 'back_mesh' : 'back_panel';

  if (effectiveJoinery === 'dowel') {
    out.push(
      { id: 'back_to_seat_left', fromPartId: backPartId, toPartId: 'leg_3', type: 'dowel', diameter: 8, length: 40, count: 2 },
      { id: 'back_to_seat_right', fromPartId: backPartId, toPartId: 'leg_4', type: 'dowel', diameter: 8, length: 40, count: 2 },
    );
  } else {
    out.push(
      { id: 'back_to_seat_left', fromPartId: backPartId, toPartId: 'leg_3', type: 'screw', size: 'M6', length: 50, count: 2 },
      { id: 'back_to_seat_right', fromPartId: backPartId, toPartId: 'leg_4', type: 'screw', size: 'M6', length: 50, count: 2 },
    );
  }

  return out;
}

export function features(intake: IntakeSpec): Feature[] {
  if (intake.qualityLevel === 'draft' || intake.qualityLevel === 'standard') return [];

  const d = getDetails(intake);
  const effectiveJoinery = d.joineryType === 'mixed' ? 'dowel' : d.joineryType;

  const out: Feature[] = [];

  // Edge roundover on seat front edge
  out.push({
    id: 'edge_seat_front',
    partId: 'seat',
    kind: 'edge_treatment',
    treatment: 'roundover',
    amount: 2,
    scope: 'all',
    position: [0, 0, 0],
    rotation: [0, 0, 0],
    paramRefs: [],
  });

  // Dowel holes at seat-to-leg joints
  if (effectiveJoinery === 'dowel') {
    for (let i = 0; i < 4; i++) {
      out.push({
        id: `dowel_hole_leg_${i + 1}`,
        partId: 'seat',
        kind: 'dowel_hole',
        diameter: 8,
        depth: 20,
        axis: 'z',
        through: false,
        position: [0, 0, 0],
        rotation: [0, 0, 0],
        paramRefs: [],
      });
    }
  }

  return out;
}
